// dependencies
// charting components
import {
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	LinearScale,
	Title,
	Tooltip,
} from "chart.js";
import { endOfDay } from "date-fns";
import React from "react";
import { Bar } from "react-chartjs-2";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import Text from "../../../../../../components/Text";
import PYToolTip from "../../../../../../components/Tooltip";
import { CardBody } from "../../../../components/Card";

// store
import useStore from "../../../../store";

// hooks
import { useNPSSummary } from "../../../../../../api/hooks";

// utils
import { getColor } from "../../../../../../utils/getColor";
import { getChartMaxY, getEnpsScoreLabel } from "../../utils";

// styles
import css from "./BreakdownCard.module.scss";

// register chartjs plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface BarChartOptions extends ChartOptions<"bar"> {
	barThickness: number;
	barBorderRadius: number;
}

const chartAreaRightBorder = {
	id: "chartAreaBorder",
	beforeDraw(chart) {
		const {
			ctx,
			chartArea: { left, top, width, height },
		} = chart;
		ctx.save();
		ctx.strokeStyle = getColor("neutral", 1);
		ctx.lineWidth = 2;

		// draw the right border
		ctx.beginPath();
		ctx.moveTo(left + width, top);
		ctx.lineTo(left + width, top + height);
		ctx.stroke();

		ctx.restore();
	},
};

const scoreRangeLabels = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const EnpsBreakdownChart = () => {
	const [filter] = useStore((state) => [state.filter]);

	// api response for the date range
	const { data, isLoading, error } = useNPSSummary({
		startDate: filter.startDate,
		endDate: filter.endDate || endOfDay(new Date()),
	});

	const npsData = data ? data.data[0] : null;
	const eNPSScore = npsData ? Math.round(npsData.eNPS_score) : null;

	const getPercentage = (breakDownValue) => {
		if (!breakDownValue || !npsData) return null;
		return (breakDownValue / npsData.count) * 100;
	};

	const getOriginalValue = (percentageValue) => {
		if (!npsData) return null;
		return Math.round((percentageValue * npsData.count) / 100);
	};

	const detractorsData = scoreRangeLabels.map((score) => {
		if (score < 7) {
			return getPercentage(npsData?.score_to_count[score]) || 0;
		} else {
			return null;
		}
	});

	const passivesData = scoreRangeLabels.map((score) => {
		if (score == 7 || score == 8) {
			return getPercentage(npsData?.score_to_count[score]) || 0;
		} else {
			return null;
		}
	});

	const promotorsData = scoreRangeLabels.map((score) => {
		if (score > 8) {
			return getPercentage(npsData?.score_to_count[score]) || 0;
		} else {
			return null;
		}
	});

	const allPercentageData = [...detractorsData, ...passivesData, ...promotorsData];
	const validPercentageData = allPercentageData.filter(
		(value): value is number => value !== null && value !== undefined
	);
	const largestPercentage = Math.max(...validPercentageData);

	const chartData: ChartData<"bar"> = {
		labels: scoreRangeLabels,
		datasets: [
			{
				label: "Detractors",
				data: detractorsData,
				backgroundColor: getColor("red", 5),
				borderWidth: 0,
			},
			{
				label: "Passives",
				data: passivesData,
				backgroundColor: getColor("yellow", 4),
				borderWidth: 0,
			},
			{
				label: "Promoters",
				data: promotorsData,
				backgroundColor: getColor("green", 5),
				borderWidth: 0,
			},
		],
	};

	// chart options
	const options: BarChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		barThickness: 12,
		barBorderRadius: 2,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				yAlign: "bottom",
				displayColors: false,
				backgroundColor: getColor("neutral", 7),
				titleFont: {
					weight: "normal",
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				bodyFont: {
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				caretPadding: 8,
				cornerRadius: 4,
				callbacks: {
					// format tooltip title
					title: (context) => {
						const percentage: number = Math.round(context[0].raw as number);
						return `Percentage: ${percentage}%`;
					},
					label: (context) => {
						if (context.raw === null) return void 0;
						const totalAnswer: number | null = getOriginalValue(context.raw as number);
						return `Answer: ${totalAnswer}`;
					},
				},
			},
		},
		scales: {
			x: {
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					maxRotation: 0,
					minRotation: 0,
					color: getColor("primary", 9),
					font: {
						size: 10,
						family: "Source Sans Pro, sans-serif",
					},
				},
				stacked: true,
			},
			y: {
				title: {
					display: true,
					text: "Percentage of Employees",
				},
				display: true,
				min: 0,
				max: getChartMaxY(largestPercentage),
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					color: getColor("primary", 9),
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					stepSize: 25,
					callback: (value) => {
						return `${value}%`;
					},
				},
				stacked: true,
			},
		},
	};

	if (isLoading) {
		return (
			<div className={css.spinnerBox}>
				<Spinner size={20} />
			</div>
		);
	}

	if (!npsData) {
		return <EmptyChart type="eNPSBreakdown" />;
	}

	if (error !== null) {
		return (
			<div className={css.spinnerBox}>
				<Text size="paragraph-3" weight="regular">
					Error fetching score
				</Text>
			</div>
		);
	}

	return (
		<CardBody>
			<div className={css.cardBody}>
				<div className={css.scoreContainer}>
					<Text element="p" size="heading-2" weight="semi-bold">
						{eNPSScore}
					</Text>
					<Text element="p" size="caption" weight="regular">
						{getEnpsScoreLabel(eNPSScore)}
					</Text>
				</div>
				<div className={css.chartContainer}>
					<div className={css.barChart}>
						<Bar data={chartData} options={options} plugins={[chartAreaRightBorder]} />
					</div>
					<div className={css.legendContainer}>
						<PYToolTip message="More likely to speak negatively about the company" placement="bottom">
							<Text
								className={`${css.legendText} ${css.detractorsText}`}
								element="span"
								size="extra-small"
								weight="semi-bold"
								color="neutral-0"
							>
								Detractors {npsData ? Math.round(getPercentage(npsData.detractors) ?? 0) : 0}%
							</Text>
						</PYToolTip>
						<PYToolTip message="Less likely to recommend the company" placement="bottom">
							<Text
								className={`${css.legendText} ${css.passivesText}`}
								element="span"
								size="extra-small"
								weight="semi-bold"
								color="neutral-0"
							>
								Passives {npsData ? Math.round(getPercentage(npsData.neutrals) ?? 0) : 0}%
							</Text>
						</PYToolTip>
						<PYToolTip message="Likely to recommend the company" placement="bottom">
							<Text
								className={`${css.legendText} ${css.promotersText}`}
								element="span"
								size="extra-small"
								weight="semi-bold"
								color="neutral-0"
							>
								Promoters {npsData ? Math.round(getPercentage(npsData.promoters) ?? 0) : 0}%
							</Text>
						</PYToolTip>
					</div>
				</div>
			</div>
		</CardBody>
	);
};

export default EnpsBreakdownChart;
